import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '../styles/GlobalStyles';
import { rv, containerWidth, mediaQueries } from '../styles/responsive';
import { Section, Container, SectionHeader, SectionLabel, SectionTitle, SectionDescription, Tag } from './ui';
import { FlaskIcon, ServerIcon, LayoutIcon } from './ui/Icons';

const pulseSlow = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const TechSection = styled(Section)`
  background-color: ${colors.white};
  padding: ${rv(80, 48)} 0;
  justify-content: center;
  align-items: center;
`;

const TechContainer = styled.div`
  width: 100%;
  max-width: ${containerWidth(1024, 24)};
  margin: 0 auto;
  padding: 0 ${rv(24, 20)};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rv(32, 40)};

  ${mediaQueries.tabletUp} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const ConnectionLine = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  left: 10%;
  right: 10%;
  height: 2px;
  background: ${colors.slate[100]};
  transform: translateY(-50%);
  z-index: 0;

  ${mediaQueries.tabletUp} {
    display: block;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to right, #bfdbfe, #818cf8, #c4b5fd);
    animation: ${pulseSlow} 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    opacity: 0.6;
  }
`;

const MobileConnectionLine = styled.div`
  position: absolute;
  left: 50%;
  top: 10%;
  bottom: 10%;
  width: 2px;
  background: ${colors.slate[100]};
  transform: translateX(-50%);
  z-index: 0;

  ${mediaQueries.tabletUp} {
    display: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, #bfdbfe, #818cf8, #c4b5fd);
    animation: ${pulseSlow} 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    opacity: 0.6;
  }
`;

const TechCard = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${mediaQueries.tabletUp} {
    width: 33.333%;
  }
`;

const IconWrapper = styled.div<{ $glowColor: string }>`
  width: ${rv(128, 80)};
  height: ${rv(128, 80)};
  background: ${colors.white};
  border-radius: ${rv(32, 24)};
  border: 1px solid ${colors.slate[100]};
  box-shadow: 0 10px 40px -10px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.5s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ $glowColor }) => $glowColor};
    border-radius: inherit;
    filter: blur(24px);
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${colors.white};
    border-radius: inherit;
    z-index: 10;
  }

  svg {
    position: relative;
    z-index: 20;
    width: ${rv(48, 32)};
    height: ${rv(48, 32)};
    transition: transform 0.5s ease;
  }

  ${TechCard}:hover & {
    transform: translateY(-8px);

    &::before {
      opacity: 0.3;
    }

    svg {
      transform: scale(1.1);
    }
  }
`;

const CardTitle = styled.h3`
  font-size: ${rv(20, 16)};
  font-weight: 700;
  color: ${colors.slate[900]};
  margin-top: ${rv(24, 20)};
  margin-bottom: ${rv(12, 12)};
  text-align: center;
`;

const TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${rv(8, 6)};
`;

const TechTag = styled(Tag)`
  background: ${colors.white};
  border: 1px solid ${colors.slate[200]};
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
`;

const techStacks = [
  {
    title: 'Data & AI Models',
    icon: FlaskIcon,
    iconColor: '#3b82f6',
    glowColor: '#3b82f6',
    tags: ['PyTorch', 'TensorFlow', 'LangChain'],
  },
  {
    title: 'Backend & Cloud',
    icon: ServerIcon,
    iconColor: '#6366f1',
    glowColor: '#6366f1',
    tags: ['Node.js', 'Python', 'AWS'],
  },
  {
    title: 'Frontend & UX',
    icon: LayoutIcon,
    iconColor: '#a855f7',
    glowColor: '#a855f7',
    tags: ['React', 'Next.js', 'Tailwind'],
  },
];

const TechStack: React.FC = () => {
  return (
    <TechSection id="techstack">
      <Container>
        <SectionHeader>
          <SectionLabel>Seamless Integration</SectionLabel>
          <SectionTitle>Tech Stack & Architecture</SectionTitle>
          <SectionDescription>
            강력한 AI 모델링부터 안정적인 백엔드, 사용자 중심의 프론트엔드까지 유기적으로 연결된 기술 생태계를 제공합니다.
          </SectionDescription>
        </SectionHeader>
      </Container>

      <TechContainer>
        <ConnectionLine />
        <MobileConnectionLine />

        {techStacks.map((stack, index) => {
          const IconComponent = stack.icon;
          return (
            <TechCard key={index}>
              <IconWrapper $glowColor={stack.glowColor}>
                <IconComponent size={48} color={stack.iconColor} />
              </IconWrapper>
              <CardTitle>{stack.title}</CardTitle>
              <TagGroup>
                {stack.tags.map((tag, tagIndex) => (
                  <TechTag key={tagIndex}>{tag}</TechTag>
                ))}
              </TagGroup>
            </TechCard>
          );
        })}
      </TechContainer>
    </TechSection>
  );
};

export default TechStack;
